import { JsonHighlight } from "@arcadeai/design-system/components/ui/atoms/json-highlight";
import { cn } from "@arcadeai/design-system/lib/utils";
import { SquareCode } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import type { ReactNode } from "react";
import { AuthErrorTerminal } from "@/app/playground/execute/components/panel/auth-alert";
import { useFrameAnimation } from "@/app/playground/execute/hooks/use-frame-animation";
import type { PlaygroundResponse } from "@/app/playground/execute/hooks/use-store";
import { HeaderActions } from "./header-actions";
import { TerminalLine } from "./terminal-line";

type TerminalState = "ready" | "loading" | "success" | "error";

type TerminalOutputProps = {
  state: TerminalState;
  response?: PlaygroundResponse | null;
  expectedOutput?: string;
  onExecute?: () => void;
  isExecuteDisabled?: boolean;
  tooltipMessage?: string | null;
  isAuthError?: boolean;
  needsAuthorization?: boolean;
  onBackToTool?: () => void;
};

const STATUS_LABEL: Record<TerminalState, string> = {
  ready: "Ready",
  loading: "Running",
  success: "Success",
  error: "Error",
};

const STATUS_MESSAGE: Record<TerminalState, string> = {
  ready: "Press Run to execute",
  loading: "Executing tool",
  success: "Output ready",
  error: "Check logs",
};

const STATUS_TONE: Record<TerminalState, string> = {
  ready: "text-neutral-500 dark:text-neutral-400",
  loading: "text-amber-600 dark:text-amber-300",
  success: "text-emerald-600 dark:text-emerald-300",
  error: "text-red-600 dark:text-red-300",
};

function ReadyState({ expectedOutput }: { expectedOutput?: string }) {
  if (!expectedOutput) {
    return (
      <motion.p
        animate={{ opacity: 1 }}
        className="font-mono text-neutral-400 text-sm dark:text-neutral-500"
        initial={{ opacity: 0 }}
      >
        Press Run to execute this tool.
      </motion.p>
    );
  }

  return (
    <motion.div
      animate={{ opacity: 1 }}
      initial={{ opacity: 0.85 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
    >
      <TerminalBlock format="txt" label="Expected Output">
        <p className="text-neutral-600 text-sm leading-relaxed dark:text-neutral-300">
          {expectedOutput}
        </p>
      </TerminalBlock>
    </motion.div>
  );
}

const LOADING_STEPS = [
  "convincing electrons to cooperate",
  "bribing the server hamsters",
  "consulting the ancient scrolls",
  "warming up the flux capacitor",
  "counting backwards from infinity",
  "untangling the spaghetti code",
  "negotiating with the API gods",
  "performing arcane rituals",
];

function LoadingState() {
  return (
    <motion.div
      animate={{ opacity: 1 }}
      className="flex items-center gap-2 font-mono text-sm"
      initial={{ opacity: 0.85 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
    >
      <TerminalSpinner className="shrink-0" />
      <RotatingText items={LOADING_STEPS} />
    </motion.div>
  );
}

function AuthErrorState() {
  return (
    <motion.div
      animate={{ opacity: 1 }}
      className="space-y-3"
      initial={{ opacity: 0.85 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
    >
      <TerminalLine color="red">
        <span className="text-neutral-500 dark:text-neutral-400">
          authorization required
        </span>
      </TerminalLine>
      <AuthErrorTerminal />
    </motion.div>
  );
}

function ResponseState({ response }: { response: PlaygroundResponse }) {
  const responseString = JSON.stringify(response, null, 2);

  return (
    <motion.div
      animate={{ opacity: 1 }}
      initial={{ opacity: 0.85 }}
      transition={{ duration: 0.15, ease: "easeOut" }}
    >
      <JsonHighlight code={responseString} />
    </motion.div>
  );
}

function TerminalContent({
  state,
  response,
  expectedOutput,
  isAuthError,
}: {
  state: TerminalState;
  response?: PlaygroundResponse | null;
  expectedOutput?: string;
  isAuthError?: boolean;
}) {
  if (state === "ready") {
    return <ReadyState expectedOutput={expectedOutput} />;
  }

  if (state === "loading") {
    return <LoadingState />;
  }

  if (isAuthError) {
    return <AuthErrorState />;
  }

  if (response) {
    return <ResponseState response={response} />;
  }

  if (state === "error") {
    return (
      <motion.div
        animate={{ opacity: 1 }}
        initial={{ opacity: 0.85 }}
        transition={{ duration: 0.15, ease: "easeOut" }}
      >
        <TerminalLine color="red">
          <span className="text-neutral-500 dark:text-neutral-400">
            An unexpected error occurred
          </span>
        </TerminalLine>
      </motion.div>
    );
  }

  return null;
}

export function TerminalOutput({
  state,
  response,
  expectedOutput,
  onExecute,
  isExecuteDisabled,
  tooltipMessage,
  isAuthError,
  needsAuthorization,
  onBackToTool,
}: TerminalOutputProps) {
  const durationLabel = response?.duration
    ? ` (${response.duration.toFixed(0)}ms)`
    : "";
  const responseString = response ? JSON.stringify(response, null, 2) : "";

  return (
    <motion.div
      className="relative flex h-full flex-col overflow-hidden rounded-xl border border-neutral-300 bg-neutral-50 shadow-sm dark:border-neutral-800/80 dark:bg-neutral-950/95 dark:shadow-[0_10px_30px_-18px_rgba(0,0,0,0.7)]"
      layout
    >
      {/* Terminal Header */}
      <div className="relative z-10 flex min-h-[46px] items-center justify-between border-neutral-200 border-b bg-neutral-100 px-4 dark:border-neutral-800/80 dark:bg-neutral-900/80">
        <div className="flex items-center gap-2">
          <SquareCode className="h-3.5 w-3.5 text-neutral-500 dark:text-neutral-400" />
          <span className="font-medium text-neutral-600 text-xs uppercase tracking-widest dark:text-neutral-300">
            Execution Output
          </span>
        </div>
        <HeaderActions
          isExecuteDisabled={isExecuteDisabled}
          needsAuthorization={needsAuthorization}
          onExecute={onExecute}
          responseString={responseString}
          state={state}
          tooltipMessage={tooltipMessage}
        />
      </div>

      {/* Terminal Content */}
      <div
        aria-live="polite"
        className="relative z-10 flex-1 overflow-auto p-4 font-mono"
        role="log"
      >
        <AnimatePresence initial={false} mode="sync">
          <TerminalContent
            expectedOutput={expectedOutput}
            isAuthError={isAuthError}
            key={isAuthError ? "auth-error" : state}
            response={response}
            state={state}
          />
        </AnimatePresence>
      </div>

      <div className="relative z-10 flex min-h-[34px] items-center justify-between border-neutral-200 border-t bg-neutral-100 px-4 text-xs dark:border-neutral-800/80 dark:bg-neutral-900/70">
        <div className="flex items-center gap-2 font-mono text-neutral-500 dark:text-neutral-400">
          <span className="text-neutral-400 dark:text-neutral-500">Status</span>
          <span className={STATUS_TONE[state]}>
            {STATUS_LABEL[state]}
            {durationLabel}
          </span>
        </div>
        {onBackToTool ? (
          <button
            className="cursor-pointer font-medium text-sky-600 hover:text-sky-700 dark:text-sky-400 dark:hover:text-sky-300"
            onClick={onBackToTool}
            type="button"
          >
            ← Edit Parameters
          </button>
        ) : (
          <div className="font-mono text-neutral-400 dark:text-neutral-500">
            {STATUS_MESSAGE[state]}
          </div>
        )}
      </div>
    </motion.div>
  );
}

// Braille spinner frames - dots light up in circular sequence
const SPINNER_FRAMES = ["⠋", "⠙", "⠹", "⠸", "⠼", "⠴", "⠦", "⠧", "⠇", "⠏"];

function TerminalSpinner({ className }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={cn(
        "relative inline-flex h-5 w-5 items-center justify-center text-lg",
        className
      )}
    >
      {/* Dim background showing all dots */}
      <span className="absolute inset-0 flex items-center justify-center text-neutral-300 dark:text-neutral-800/60">
        ⣿
      </span>
      {/* Active dots in bright color with glow */}
      <span className="relative text-amber-500 drop-shadow-[0_0_6px_rgba(245,158,11,0.6)] dark:text-yellow-300 dark:drop-shadow-[0_0_6px_rgba(253,224,71,0.8)]">
        <FrameAnimator frames={SPINNER_FRAMES} interval={80} />
      </span>
    </span>
  );
}

function FrameAnimator({
  frames,
  interval,
}: {
  frames: string[];
  interval: number;
}) {
  const index = useFrameAnimation({
    frameCount: frames.length,
    interval,
  });

  return <>{frames[index]}</>;
}

const ROTATING_TEXT_INTERVAL = 1500;

function RotatingText({ items }: { items: string[] }) {
  const index = useFrameAnimation({
    frameCount: items.length,
    interval: ROTATING_TEXT_INTERVAL,
    randomStart: true,
  });

  return (
    <motion.span
      animate={{ opacity: 1 }}
      className="text-neutral-500 dark:text-neutral-400"
      initial={{ opacity: 0 }}
      key={items[index]}
      transition={{ duration: 0.3 }}
    >
      {items[index]}
    </motion.span>
  );
}

function TerminalBlock({
  label,
  description,
  format = "json",
  children,
}: {
  label: string;
  description?: string;
  format?: "json" | "txt";
  children: ReactNode;
}) {
  return (
    <div className="overflow-hidden rounded-lg border border-neutral-200 bg-neutral-100/60 backdrop-blur dark:border-neutral-800/80 dark:bg-neutral-950/60">
      <div className="flex items-center justify-between border-neutral-200 border-b bg-neutral-100/80 px-3 py-2 text-xs dark:border-neutral-800/60 dark:bg-neutral-950/40">
        <div className="flex items-center gap-2 font-mono text-neutral-500 dark:text-neutral-400">
          <span className="rounded-md border border-neutral-300 bg-neutral-200/60 px-1.5 py-0.5 text-[10px] text-neutral-600 uppercase tracking-wider dark:border-neutral-700/80 dark:bg-neutral-900/60 dark:text-neutral-300">
            {format}
          </span>
          <span>{label}</span>
        </div>
        {description ? (
          <span className="text-neutral-400 dark:text-neutral-500">
            {description}
          </span>
        ) : null}
      </div>
      <div className="p-3">{children}</div>
    </div>
  );
}
